import type { LineItemEndDateFilter, RangeFilter, TraffickingFilters } from '../../../apis/graphql';
import { ComaprisonOperation, LineItemEndDateFilterUnit } from '../../../apis/graphql';
import { TRAFFICKING_SEARCH_PARAMS, TRAFFICKING_TABLE_NAMES } from '../../../constants';
import type { NonNullableFields } from '../../../models';
import { makeGroupedDateOptions } from '../../../pages/Trafficking/TableFilterBar/DateFilter';
import type { TableFilters } from './types';

export const DEFAULT_DATE_FILTER = makeGroupedDateOptions()[1].options[0]; // 'Running This Month'

export const LINE_ITEMS_STATUSES = {
  ON: 'ON',
  OFF: 'OFF',
  CANCELLED: 'CANCELLED',
  PAUSED: 'PAUSED',
};

export const LINE_ITEMS_SUB_STATUSES = {
  READY: 'READY',
  LIVE: 'LIVE',
  LATE: 'LATE',
  ENDED: 'ENDED',
  DID_NOT_RUN: 'DID_NOT_RUN',
};

const DEFAULT_TAB = TRAFFICKING_TABLE_NAMES[0]; // 'campaigns'
export const DEFAULT_RANGE_FILTER_VALUE: RangeFilter = {
  min: null,
  max: null,
  minOperator: null,
  maxOperator: null,
};

export const DEFAULT_EMPTY_NUMBER_FILTER_VALUE = Number.MIN_SAFE_INTEGER;
export const DEFAULT_LINE_ITEM_END_DATE_FILTER_VALUE: LineItemEndDateFilter = {
  operator: null,
  date: null,
  startDate: null,
  endDate: null,
  comparisonOperation: ComaprisonOperation.LessThan,
  unit: LineItemEndDateFilterUnit.Day,
  duration: DEFAULT_EMPTY_NUMBER_FILTER_VALUE,
};

export const INITIAL_TABLE_FILTERS: NonNullableFields<TableFilters> = TRAFFICKING_TABLE_NAMES.reduce(
  (acc, tableName) => {
    return {
      ...acc,
      [tableName]: {
        searchTerm: '',
        selectedRowIds: [],
        sortBy: null,
      },
    };
  },
  {} as NonNullableFields<TableFilters>
);

export const TRAFFICKING_FILTERS_SEARCH_PARAMS = [
  TRAFFICKING_SEARCH_PARAMS.SELECTED_TAB,
  TRAFFICKING_SEARCH_PARAMS.DATE_FILTER,
  TRAFFICKING_SEARCH_PARAMS.LINE_ITEMS_SELECTED_IDS,
  TRAFFICKING_SEARCH_PARAMS.ADS_ITEMS_SELECTED_IDS,
];

export const TRAFFICKING_INITIAL_TABLE_FILTERS = [
  TRAFFICKING_SEARCH_PARAMS.DATE_FILTER,
  TRAFFICKING_SEARCH_PARAMS.SELECTED_TAB,
];

export const BOOLEAN_TABLE_FILTERS: Array<keyof TraffickingFilters> = [TRAFFICKING_SEARCH_PARAMS.IS_MAKEGOOD];

export const INITIAL_FILTERS = {
  [TRAFFICKING_SEARCH_PARAMS.DATE_FILTER]: DEFAULT_DATE_FILTER,
  [TRAFFICKING_SEARCH_PARAMS.SELECTED_TAB]: DEFAULT_TAB,
  [TRAFFICKING_SEARCH_PARAMS.ACCOUNT_EXECUTIVE]: null,
  [TRAFFICKING_SEARCH_PARAMS.PRODUCT_TYPE]: null,
  [TRAFFICKING_SEARCH_PARAMS.ACCOUNT_MANAGER]: null,
  [TRAFFICKING_SEARCH_PARAMS.PACING_RISK_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.HAS_MISALIGNED_SCHEDULE]: null,
  [TRAFFICKING_SEARCH_PARAMS.HAS_ZERO_DELIVERY_RISK]: null,
  [TRAFFICKING_SEARCH_PARAMS.GENERAL_PACING_STATUS_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.REVIEW_STATUS_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.ORDER_TYPE_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.AD_STATUS_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_STATUS_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.TIER_NAME_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.PRIORITY_VALUE_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.BILLABLE_THIRD_PARTY_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.PUBLISHER_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.AD_TYPE_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.CREATIVE_TYPE_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.CREATIVE_REVIEW_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.IS_COPPA_OR_CARRU]: null,
  [TRAFFICKING_SEARCH_PARAMS.IS_MAKEGOOD]: null,
  [TRAFFICKING_SEARCH_PARAMS.IS_ADDED_VALUE]: null,
  [TRAFFICKING_SEARCH_PARAMS.BRAND_ID_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.EXCLUDE_BRAND_ID_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.AGENCY_ID_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.INDUSTRY_ID_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.CURRENCY_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.IS_ROTATION]: null,
  [TRAFFICKING_SEARCH_PARAMS.IS_SEQUENCE]: null,
  [TRAFFICKING_SEARCH_PARAMS.COUNTRY_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.DO_NOT_TRAFFIC]: null,
  [TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_TYPE_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.PERCENT_PACING]: null,
  [TRAFFICKING_SEARCH_PARAMS.COMPLETIONS_REMAINING]: null,
  [TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_END_DATE]: null,
  [TRAFFICKING_SEARCH_PARAMS.SSP_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.COST_METHOD_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.CUSTOM_PACING_APPLIED]: null,
  [TRAFFICKING_SEARCH_PARAMS.BILLABLE_EVENT_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.DISTRIBUTION_PARTNER_LIST]: null,
  [TRAFFICKING_SEARCH_PARAMS.CPM]: null,
  [TRAFFICKING_SEARCH_PARAMS.FREQUENCY_CAP]: null,
  // V2 filter keys — temporary bridge until V1 code is removed (see cleanup task)
  traffickerEmailList: null,
  accountExecutiveList: null,
  adProductList: null,
  ...INITIAL_TABLE_FILTERS,
} as const;
